/**
 * Notification Store for Advisor Panel
 * Manages notifications with persistence and real-time updates
 */

import { create } from "zustand";
import { persist } from "zustand/middleware";
import type {
  Notification,
  NotificationSettings,
  NotificationType,
  NotificationPriority,
} from "../types";
import { DEFAULT_NOTIFICATION_SETTINGS } from "../types";

interface NotificationState {
  notifications: Notification[];
  settings: NotificationSettings;
  unreadCount: number;
  isDropdownOpen: boolean;

  // Actions
  addNotification: (
    notification: Omit<Notification, "id" | "timestamp" | "read">
  ) => void;
  removeNotification: (id: string) => void;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  clearAll: () => void;
  clearRead: () => void;
  updateSettings: (settings: Partial<NotificationSettings>) => void;
  setDropdownOpen: (isOpen: boolean) => void;

  // Helpers
  getUnreadCount: () => number;
  getRecentNotifications: (limit?: number) => Notification[];
}

// Generate unique ID
function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

// Play notification sound
function playNotificationSound(): void {
  try {
    const audio = new Audio("/sounds/notification.mp3");
    audio.volume = 0.5;
    audio.play().catch(() => {
      // Ignore autoplay restrictions
    });
  } catch {
    // Ignore audio errors
  }
}

// Check if we're in quiet hours
function isQuietHours(settings: NotificationSettings): boolean {
  if (!settings.quietHours?.enabled) return false;

  const now = new Date();
  const currentTime = `${now.getHours().toString().padStart(2, "0")}:${now
    .getMinutes()
    .toString()
    .padStart(2, "0")}`;

  const { from, to } = settings.quietHours;

  // Handle overnight quiet hours (e.g., 22:00 to 08:00)
  if (from > to) {
    return currentTime >= from || currentTime < to;
  }

  return currentTime >= from && currentTime < to;
}

// Request browser notification permission
async function requestNotificationPermission(): Promise<boolean> {
  if (!("Notification" in window)) {
    return false;
  }

  if (Notification.permission === "granted") {
    return true;
  }

  if (Notification.permission !== "denied") {
    const permission = await Notification.requestPermission();
    return permission === "granted";
  }

  return false;
}

// Show browser notification
async function showBrowserNotification(
  notification: Notification,
  settings: NotificationSettings
): Promise<void> {
  if (!settings.desktopEnabled || isQuietHours(settings)) {
    return;
  }

  const hasPermission = await requestNotificationPermission();
  if (!hasPermission) return;

  const browserNotification = new Notification(notification.title, {
    body: notification.message,
    icon: "/favicon.ico",
    tag: notification.id,
    requireInteraction: notification.priority === "urgent",
  });

  browserNotification.onclick = () => {
    window.focus();
    if (notification.link) {
      window.location.href = notification.link;
    }
    browserNotification.close();
  };

  // Auto-close after 5 seconds for non-urgent notifications
  if (notification.priority !== "urgent") {
    setTimeout(() => browserNotification.close(), 5000);
  }
}

export const useNotificationStore = create<NotificationState>()(
  persist(
    (set, get) => ({
      notifications: [],
      settings: DEFAULT_NOTIFICATION_SETTINGS,
      unreadCount: 0,
      isDropdownOpen: false,

      addNotification: (notificationData) => {
        const state = get();

        // Check if notification type is enabled
        if (!state.settings.enabledTypes.includes(notificationData.type)) {
          return;
        }

        const notification: Notification = {
          ...notificationData,
          id: generateId(),
          timestamp: new Date().toISOString(),
          read: false,
        };

        // Add to beginning of array (newest first)
        set((state) => ({
          notifications: [notification, ...state.notifications].slice(0, 100), // Keep max 100
          unreadCount: state.unreadCount + 1,
        }));

        // Play sound if enabled
        if (state.settings.soundEnabled && !isQuietHours(state.settings)) {
          playNotificationSound();
        }

        // Show browser notification
        showBrowserNotification(notification, state.settings);

        // Auto-dismiss if configured
        if (notification.autoDismissMs && notification.autoDismissMs > 0) {
          setTimeout(() => {
            get().removeNotification(notification.id);
          }, notification.autoDismissMs);
        }
      },

      removeNotification: (id) => {
        set((state) => {
          const notification = state.notifications.find((n) => n.id === id);
          const wasUnread = notification && !notification.read;

          return {
            notifications: state.notifications.filter((n) => n.id !== id),
            unreadCount: wasUnread
              ? Math.max(0, state.unreadCount - 1)
              : state.unreadCount,
          };
        });
      },

      markAsRead: (id) => {
        set((state) => {
          const notification = state.notifications.find((n) => n.id === id);
          if (!notification || notification.read) return state;

          return {
            notifications: state.notifications.map((n) =>
              n.id === id ? { ...n, read: true } : n
            ),
            unreadCount: Math.max(0, state.unreadCount - 1),
          };
        });
      },

      markAllAsRead: () => {
        set((state) => ({
          notifications: state.notifications.map((n) => ({ ...n, read: true })),
          unreadCount: 0,
        }));
      },

      clearAll: () => {
        set({ notifications: [], unreadCount: 0 });
      },

      clearRead: () => {
        set((state) => ({
          notifications: state.notifications.filter((n) => !n.read),
        }));
      },

      updateSettings: (newSettings) => {
        set((state) => ({
          settings: { ...state.settings, ...newSettings },
        }));
      },

      setDropdownOpen: (isOpen) => {
        set({ isDropdownOpen: isOpen });
      },

      getUnreadCount: () => {
        return get().unreadCount;
      },

      getRecentNotifications: (limit = 10) => {
        return get().notifications.slice(0, limit);
      },
    }),
    {
      name: "advisor-notifications",
      partialize: (state) => ({
        notifications: state.notifications.slice(0, 50), // Persist max 50
        settings: state.settings,
        unreadCount: state.unreadCount,
      }),
    }
  )
);

// Helper functions for creating notifications
export function createLeadNotification(
  leadName: string,
  creditType: string,
  leadId: string
): Omit<Notification, "id" | "timestamp" | "read"> {
  return {
    type: "new_lead",
    title: "Nowy lead!",
    message: `${leadName} szuka kredytu ${creditType}`,
    priority: "high",
    link: `/leads?id=${leadId}`,
    autoDismissMs: 0,
  };
}

export function createSessionNotification(
  type: "started" | "message" | "ended",
  clientName: string,
  sessionId: string
): Omit<Notification, "id" | "timestamp" | "read"> {
  const configs = {
    started: {
      type: "session_started" as NotificationType,
      title: "Sesja rozpoczęta",
      message: `${clientName} dołączył do sesji`,
      priority: "normal" as NotificationPriority,
    },
    message: {
      type: "session_message" as NotificationType,
      title: "Nowa wiadomość",
      message: `${clientName} wysłał wiadomość`,
      priority: "normal" as NotificationPriority,
    },
    ended: {
      type: "session_ended" as NotificationType,
      title: "Sesja zakończona",
      message: `Sesja z ${clientName} została zakończona`,
      priority: "low" as NotificationPriority,
    },
  };

  const config = configs[type];
  return {
    ...config,
    link: `/sessions/${sessionId}`,
    autoDismissMs: type === "ended" ? 10000 : 0,
  };
}

export function createCreditsNotification(
  amount: number
): Omit<Notification, "id" | "timestamp" | "read"> {
  return {
    type: "credits_assigned",
    title: "Kredyty przyznane",
    message: `Otrzymałeś ${amount} kredytów`,
    priority: "normal",
    autoDismissMs: 15000,
  };
}

export function createSystemNotification(
  title: string,
  message: string,
  priority: NotificationPriority = "normal"
): Omit<Notification, "id" | "timestamp" | "read"> {
  return {
    type: "system",
    title,
    message,
    priority,
    autoDismissMs: priority === "low" ? 10000 : 0,
  };
}
